import {
	UploadFileArgs,
	useUploadFile,
} from '@dealstack/frontend/shared/components/DragAndDrop/useUploadFile';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { ProgressBar } from '@dealstack/frontend/shared/components/fundament/ProgressBar/ProgressBar';
import { useTranslationDefault } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { MakeRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper/types.ts';
import { Text, rem } from '@mantine/core';
import { UseMutationResult } from '@tanstack/react-query';
import { memo, useEffect } from 'react';
import { DropzoneOptions, FileRejection, useDropzone } from 'react-dropzone';
import styled from 'styled-components';

interface Props extends DropzoneOptions {
	className?: string;
	/**
	 * Sometimes it may happen that we need to reset this component
	 * but we don't want to unmount and remount it.
	 * In such cases, just change refresh key that will restart this component
	 * to it's default state
	 */
	refreshKey?: unknown;
	onDrop?: (acceptedFiles: File[]) => void;
	uploadFileRequest?: MakeRequest<UploadFileArgs>; // use it to auto upload files on drop with showing progress bar
	uploadFileQueryRequest?: UseMutationResult; // use it to auto upload files on drop with showing progress bar
	invalidTypeMessage?: string;
	/** Data test ID for E2E testing - applied to the file input element */
	dataTestId?: string;
}

const SquareUploader = ({
	refreshKey,
	onDrop,
	uploadFileRequest,
	uploadFileQueryRequest,
	className,
	invalidTypeMessage,
	accept,
	dataTestId,
	...props
}: Props) => {
	const tDefault = useTranslationDefault();

	const { onUpload, percentComplete, setPercentComplete, isUploadError, isUploadSuccess } =
		useUploadFile({
			uploadFileRequest,
			uploadFileQueryRequest,
		});

	useEffect(() => {
		//reset progress after 2 seconds when file uploaded
		if (percentComplete === 100) {
			setTimeout(() => {
				setPercentComplete(0);
			}, 2000);
		}
	}, [percentComplete]);

	const onFilesDrop = (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
		if (!acceptedFiles?.length || rejectedFiles?.length) {
			return;
		}

		const shouldUploadOnDrop = !!uploadFileRequest || !!uploadFileQueryRequest;

		if (shouldUploadOnDrop) {
			acceptedFiles.forEach((file) => onUpload(file));
		} else {
			onDrop && onDrop(acceptedFiles);
		}
	};

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop: onFilesDrop,
		onDropRejected: () => showToastV2({ state: 'error', message: invalidTypeMessage }),
		accept,
		...props,
	});

	useEffect(() => {
		uploadFileRequest?.resetInstance();
		uploadFileQueryRequest?.reset();
	}, [refreshKey]);

	const getUploadStatusKey = () => {
		if (isUploadError) {
			return 'error_upload';
		}
		if (isUploadSuccess) {
			return 'successfully_uploaded';
		}
		return 'uploading_file';
	};

	return (
		<div className={isDragActive ? `${className} drag-active` : className} {...getRootProps()}>
			<input {...getInputProps()} data-testid={dataTestId} />
			{percentComplete > 0 ? (
				<div style={{ width: '100%' }}>
					<ProgressBar percent={percentComplete} isError={isUploadError} />
					<Text fz={11} c="dark.0" mt="0.125rem">
						{tDefault(getUploadStatusKey())}
					</Text>
				</div>
			) : (
				<UploadIconWithHalo />
			)}
			<Text size="B6Bold" c="blue.6" mt="auto">
				{tDefault('click_to_upload')}
			</Text>
			<Text size="B6medium" c="dark.0">
				{tDefault('or_drag_and_drop_documents_here')}
			</Text>
		</div>
	);
};

const UploadIconWithHalo = styled(({ className }: { className?: string }) => {
	return (
		<div className={className}>
			<Icon variant="upload-v3-thin" size="lg" />
		</div>
	);
})`
	width: ${rem(40)};
	height: ${rem(40)};
	border-radius: 50%;
	border: 6px solid ${({ theme }) => theme.v3.colors.gray[0]};
	background-color: ${({ theme }) => theme.v3.colors.gray[2]};

	${Icon} {
		margin-top: ${rem(7)};
		path {
			fill: ${({ theme }) => theme.v3.colors.dark[4]};
		}
	}
`;

const Memoized = memo(SquareUploader);
const Styled = styled(Memoized)`
	width: ${rem(160)};
	height: ${rem(152)};
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
	padding: 1rem 0.5rem;
	border: 1px dashed ${({ theme }) => theme.v3.colors.blue[6]};
	border-radius: 0.5rem;
	cursor: pointer;

	${ProgressBar} {
		align-self: flex-start;
		margin-top: 0.5rem;
		height: ${rem(5)};
	}

	&.drag-active {
		background-color: ${({ theme }) => theme.v3.colors.blue[0]};
	}
`;

export { Styled as SquareUploader };
